import { z } from 'zod';
import { jsonSchema } from './json';
import {
  buildColumnSelectionFromSchema,
  buildRelationSelectionFromSchemas,
} from './relationSelection';
import { claimSummarySchema } from './baseClaim';
import { patientSummarySchema } from './basePatient';
import { healthcardSummarySchema } from './healthcard';
import { providerSummarySchema } from './provider';
import { siteSummarySchema } from './site';
import { tariffSummarySchema } from './tariff';
import { userSummarySchema } from './user';

export const claimQueryStatusValues = [
  'CREATED',
  'SUBMITTED',
  'PENDING',
  'COMPLETED',
  'FAILED',
] as const;

export const claimQueryTypeValues = [
  'PR',
  'IP',
  'WQ',
  'IB',
  'OT',
  'FA',
  'SCI',
] as const;

export const claimQueryStatus = z.enum(claimQueryStatusValues);

export const claimQueryType = z.enum(claimQueryTypeValues);

export const adjustmentQueryType = claimQueryType.exclude(['SCI']);

export const claimQueryRequiresAllServices = (
  queryType: z.infer<typeof claimQueryType>
): boolean => ['IP', 'WQ', 'IB', 'PR'].includes(queryType);

export const CLAIM_QUERY_COMMENT_MAX_LENGTH = 250;

export const pollableClaimQueryStatusValues = [
  'SUBMITTED',
  'PENDING',
] as const satisfies ReadonlyArray<(typeof claimQueryStatusValues)[number]>;

export const pollableClaimQueryStatus = z.enum(pollableClaimQueryStatusValues);

export const claimQuerySchema = z.object({
  id: z.guid(),
  claimID: z.guid(),
  attachmentID: z.guid().nullable().optional(),
  createdBy: z.guid(),
  siteID: z.guid(),
  queryType: claimQueryType,
  status: claimQueryStatus,
  submittedPayload: jsonSchema.nullable().optional(),
  statusRequestPayload: jsonSchema.nullable().optional(),
  rawResponse: jsonSchema.nullable().optional(),
  lastPolledAt: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string().nullable().optional(),
});

export const claimQueryClaimTariffSummarySchema = z
  .object({
    tariffID: z.guid(),
    tariff: tariffSummarySchema,
  })
  .strict();

const claimQueryClaimColumnSummarySchema = claimSummarySchema
  .extend({
    // API responses serialize the database Date as an ISO string.
    serviceStart: z.string(),
  })
  .strict();

export const claimQueryClaimSummarySchema = claimQueryClaimColumnSummarySchema
  .extend({
    patient: patientSummarySchema.nullable(),
    provider: providerSummarySchema.nullable(),
    healthcard: healthcardSummarySchema.nullable(),
    tariffs: z.array(claimQueryClaimTariffSummarySchema),
  })
  .strict();

export const claimQueryRecordSchema = claimQuerySchema.extend({
  claim: claimQueryClaimSummarySchema.nullable().optional(),
  site: siteSummarySchema.nullable().optional(),
  createdByUser: userSummarySchema.nullable().optional(),
});

export const claimQueryStatusRequestPayloadSchema = z.object({
  claimId: z.string().optional(),
  userId: z.string().optional(),
  microFilmNumber: z.string().min(1),
  phinId: z.string().min(1),
  province: z.string().optional(),
  billingId: z.string().min(1),
  userSite: z.string().min(1),
  queryType: z.string().min(1),
  querySubDate: z.string().min(1),
  regNo: z.string().min(1).optional(),
});

export const manitobaClaimQuerySearchItemSchema = z.object({
  status: z.string(),
  remarks: z.string(),
});

export const manitobaClaimQueryEobDescriptionSchema = z.object({
  id: z.union([z.string(), z.number()]).nullable().optional(),
  eobCode: z.string(),
  eobCodeDesc: z.string(),
  updatedBy: z.string().nullable().optional(),
  updatedDate: z.string().nullable().optional(),
});

export const manitobaClaimQueryStatusResponseItemSchema = z.object({
  querySearchItem: manitobaClaimQuerySearchItemSchema,
});

export const manitobaClaimQueryResponseItemSchema = z.object({
  itemNo: z.string().nullable().optional(),
  serviceCode: z.string().nullable().optional(),
  serviceDate: z.string().nullable().optional(),
  processedDate: z.string().nullable().optional(),
  billAmt: z.string().nullable().optional(),
  billServices: z.string().nullable().optional(),
  paymentAmount: z.string().nullable().optional(),
  paidServices: z.string().nullable().optional(),
  eobCode: z.string().nullable().optional(),
  microFilmNumber: z.string().nullable().optional(),
  querySearchItem: manitobaClaimQuerySearchItemSchema,
  userSiteid: z.string().nullable().optional(),
  externalClaimId: z.string().nullable().optional(),
  queryType: z.string().nullable().optional(),
  givenName: z.string().nullable().optional(),
  surName: z.string().nullable().optional(),
  eobDescription: z
    .array(manitobaClaimQueryEobDescriptionSchema)
    .nullable()
    .optional(),
});

export const manitobaClaimQueryStatusResponseSchema = z.object({
  status: z.number(),
  message: z.string(),
  response: z.array(z.unknown()),
});

export const manitobaClaimQuerySubmissionResponseSchema = z
  .object({
    status: z.number().optional(),
    message: z.string().optional(),
    response: jsonSchema.optional(),
    evstatus: z.string().optional(),
    evmessage: z.string().optional(),
  })
  .loose();

export const isSuccessfulManitobaClaimQuerySubmission = (
  response: unknown
): boolean => {
  const parsed = manitobaClaimQuerySubmissionResponseSchema.safeParse(response);

  return (
    parsed.success &&
    (parsed.data.status === 200 || parsed.data.evstatus === 'S')
  );
};

const MANITOBA_SUPPORTING_DOCUMENT_NOT_REQUESTED_MESSAGE =
  'Supporting document not requested on this claim';

export const isManitobaSupportingDocumentNotRequestedResponse = (
  response: unknown
): boolean => {
  const parsed = manitobaClaimQuerySubmissionResponseSchema.safeParse(response);

  return (
    parsed.success &&
    parsed.data.evstatus?.trim() === '' &&
    parsed.data.evmessage?.trim().toLowerCase() ===
      MANITOBA_SUPPORTING_DOCUMENT_NOT_REQUESTED_MESSAGE.toLowerCase()
  );
};

// Backend claim-query service uses this to derive Drizzle relation selections
// from the same summary schemas that define the validated response contract.
// These top-level relations only require scalar columns, so their selections
// can be generated directly from their reusable summary schemas.
const claimQuerySummaryRelations = buildRelationSelectionFromSchemas({
  createdByUser: userSummarySchema,
  site: siteSummarySchema,
});

export const claimQueryRecordRelations = {
  // Claim requires an explicit selection because its API summary combines
  // scalar Claim columns with nested relations.
  claim: {
    // Select only fields stored directly on the Claim table.
    columns: buildColumnSelectionFromSchema(claimQueryClaimColumnSummarySchema),
    // Load related summaries separately through Drizzle's `with` option.
    with: {
      patient: {
        columns: buildColumnSelectionFromSchema(patientSummarySchema),
      },
      provider: {
        columns: buildColumnSelectionFromSchema(providerSummarySchema),
      },
      healthcard: {
        columns: buildColumnSelectionFromSchema(healthcardSummarySchema),
      },
      tariffs: {
        columns: {
          tariffID: true,
        },
        with: {
          tariff: {
            columns: buildColumnSelectionFromSchema(tariffSummarySchema),
          },
        },
      },
    },
  },
  ...claimQuerySummaryRelations,
};

export const claimQueryPaymentDetails = z.object({
  tariffID: z.guid(),
  serviceCode: z.string(),
  serviceDate: z.string(),
  paymentDate: z.string(),
  billAmount: z.string(),
  paymentAmount: z.string(),
  units: z.string(),
});

export const claimQueryReasonSchema = z.enum([
  'underpaid',
  'overpaid',
  'paymentNotListed',
]);

export const claimQueryDataSchema = z
  .object({
    lastName: z.string(),
    firstName: z.string(),
    sex: z.string(),
    providerLicense: z.string(),
    siteNumber: z.string(),
    phin: z.string(),
    province: z.string(),
    dateOfBirth: z.string(), // YYYY-MM-DD
    provider: z.string(),
    postalCode: z.string(),
    microfilm: z.string(),
    remittanceDate: z.string(), // YYYY-MM-DD
    claimNumber: z.string(),
    tariffs: z.array(claimQueryPaymentDetails).min(1),
    serviceDate: z.string(),
    comment: z.string(),
    date: z.string(), // form date
    providerComments: z.string().optional().nullable(),
    queryReason: claimQueryReasonSchema,
    queryType: claimQueryType.default('OT'),
  })
  .superRefine((data, ctx) => {
    if (!data.comment.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'Comment is required for Manitoba claim queries.',
        path: ['comment'],
      });
    }
  });

export const electronicClaimQueryDataSchema = z.object({
  ...claimQueryDataSchema.shape,
  queryType: adjustmentQueryType.default('OT'),
  queryReason: claimQueryReasonSchema.optional(),
  comment: z.string().trim().min(1).max(CLAIM_QUERY_COMMENT_MAX_LENGTH),
  claimNumber: z.string().trim().min(1),
  microfilm: z.string().trim().min(1),
  phin: z.string().trim().min(1),
  province: z.string().trim().min(1),
  providerLicense: z.string().trim().min(1),
  tariffs: z
    .array(
      claimQueryPaymentDetails.extend({
        serviceCode: z.string().trim().min(1),
        serviceDate: z.string().trim().min(1),
        billAmount: z.string().trim().min(1),
      })
    )
    .min(1),
});

export type ElectronicClaimQueryData = z.infer<
  typeof electronicClaimQueryDataSchema
>;

export type ClaimQuery = z.infer<typeof claimQuerySchema>;
export type ClaimQueryRecord = z.infer<typeof claimQueryRecordSchema>;
export type ClaimQueryStatusRequestPayload = z.infer<
  typeof claimQueryStatusRequestPayloadSchema
>;
export type ManitobaClaimQuerySearchItem = z.infer<
  typeof manitobaClaimQuerySearchItemSchema
>;
export type ManitobaClaimQueryEobDescription = z.infer<
  typeof manitobaClaimQueryEobDescriptionSchema
>;
export type ManitobaClaimQueryStatusResponseItem = z.infer<
  typeof manitobaClaimQueryStatusResponseItemSchema
>;
export type ManitobaClaimQuerySubmissionResponse = z.infer<
  typeof manitobaClaimQuerySubmissionResponseSchema
>;
export type ManitobaClaimQueryResponseItem = z.infer<
  typeof manitobaClaimQueryResponseItemSchema
>;
export type PollableClaimQueryStatus = z.infer<typeof pollableClaimQueryStatus>;
export type ClaimQueryPaymentDetails = z.infer<typeof claimQueryPaymentDetails>;
export type ClaimQueryData = z.infer<typeof claimQueryDataSchema>;

export const extractManitobaClaimQueryResponseItems = (
  value: unknown
): ManitobaClaimQueryResponseItem[] => {
  const parsedResponse =
    manitobaClaimQueryStatusResponseSchema.safeParse(value);
  if (!parsedResponse.success) return [];

  return parsedResponse.data.response.flatMap((item) => {
    const parsedItem = manitobaClaimQueryResponseItemSchema.safeParse(item);
    return parsedItem.success ? [parsedItem.data] : [];
  });
};

export const extractManitobaClaimQueryRemarks = (value: unknown): string[] => {
  const parsedResponse =
    manitobaClaimQueryStatusResponseSchema.safeParse(value);
  if (!parsedResponse.success) return [];

  const remarks = parsedResponse.data.response.flatMap((item) => {
    const parsedItem =
      manitobaClaimQueryStatusResponseItemSchema.safeParse(item);
    if (!parsedItem.success) return [];

    const remark = parsedItem.data.querySearchItem.remarks.trim();
    return remark ? [remark] : [];
  });

  return Array.from(new Set(remarks));
};
